import React, { useCallback } from 'react';

import { Box, Typography } from '@mui/material';

import { PropertyTypeID } from '@klokhuis/core/ApiClient/Klokhuis';
import { useIntl } from '@klokhuis/core/Intl';
import { ListingsContext } from '@klokhuis/modules/Properties/context/ListingsContext';
import { useFilterStateDispatcher } from '@klokhuis/modules/Properties/hooks/useFilterStateDispatcher';
import { PropertyTypeIDTranslations } from '@klokhuis/modules/Properties/translations';
import { getEnumValues } from '@klokhuis/utilities/Enum';

import styles from './PropertyTypeFilter.module.css';

export const PropertyTypeFilter: React.FC = () => {
  const { T } = useIntl();
  const { listingsState } = ListingsContext.useContext();
  const { filterStateDispatcher } = useFilterStateDispatcher();

  const currentFilters = listingsState.filterResult.filters;
  const selectedTypes = currentFilters.listingTypeIds || [];

  const handleFilterChange = useCallback(
    (propertyType: PropertyTypeID | 'featured') => {
      if (propertyType === 'featured') {
        filterStateDispatcher({
          ...currentFilters,
          listingTypeIds: null,
          pageNo: 1,
        });
        return;
      }

      let newListingTypeIds: PropertyTypeID[] = [];

      if (selectedTypes.includes(propertyType)) {
        newListingTypeIds = selectedTypes.filter(id => id !== propertyType);
      } else {
        newListingTypeIds = [propertyType]; // Single select to match design underline behavior better
      }

      filterStateDispatcher({
        ...currentFilters,
        listingTypeIds: newListingTypeIds.length > 0 ? newListingTypeIds : null,
        pageNo: 1,
      });
    },
    [selectedTypes, currentFilters, filterStateDispatcher],
  );

  const propertyTypeIcons: Record<string, string> = {
    featured: '/images/star-icon.svg',
    [PropertyTypeID.FarmLand]: '/images/tree-icon.svg',
    [PropertyTypeID.HouseOrVilla]: '/images/home1-icon.svg',
    [PropertyTypeID.ResidentialLand]: '/images/home2-icon.svg',
    [PropertyTypeID.CommercialLand]: '/images/building-icon.svg',
  };

  const propertyTypes = getEnumValues(PropertyTypeID);

  const renderItem = (label: string, id: PropertyTypeID | 'featured') => {
    const isActive =
      id === 'featured'
        ? selectedTypes.length === 0
        : selectedTypes.includes(id as PropertyTypeID);
    const iconSrc = propertyTypeIcons[id];
    return (
      <Box
        key={id}
        className={`${styles.filterItem} ${isActive ? styles.active : ''}`}
        onClick={() => handleFilterChange(id)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleFilterChange(id);
          }
        }}
      >
        <Box className={styles.iconWrapper}>
          {iconSrc && <img src={iconSrc} alt={label} />}
        </Box>
        <Typography variant="body1" className={styles.itemLabel}>
          {label}
        </Typography>
        {isActive && <Box className={styles.activeUnderline} />}
      </Box>
    );
  };

  return (
    <Box className={styles.root}>
      <Box className={styles.scroller}>
        {renderItem('New', 'featured')}

        {propertyTypes.map((type: PropertyTypeID) =>
          renderItem(T(PropertyTypeIDTranslations[type]), type),
        )}
      </Box>
    </Box>
  );
};

export default PropertyTypeFilter;
